import { useCallback, useState, useMemo } from 'react';

import {
    getRegisteredFlagNames,
    isRegisteredFlag,
    getRegisteredFlag,
} from './featureFlagRegistry';
import { FORCE_ON_KEY, FORCE_OFF_KEY, CUSTOM_FLAGS_KEY } from './constants';

const readLocalStorageFlags = (key) => {
    try {
        const parsed = JSON.parse(localStorage.getItem(key) || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeLocalStorageFlags = (key, flags) => {
    localStorage.setItem(key, JSON.stringify(flags));
};

const readUrlFlags = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const eParam = urlParams.get('e');

    const enabledFlags = [];
    const disabledFlags = [];

    if (eParam) {
        const flags = eParam
            .split(',')
            .map((f) => f.trim())
            .filter(Boolean);
        flags.forEach((flag) => {
            if (flag.startsWith('-')) {
                disabledFlags.push(flag.substring(1));
            } else {
                enabledFlags.push(flag);
            }
        });
    }

    return { enabledFlags, disabledFlags };
};

/**
 * Get all feature flags from Cloud SOAR's APP_INFO global
 * Returns an object with flag names as keys and enabled state as values
 */
const getAppInfoFlags = () => {
    // APP_INFO is a global set by the backend in the HTML
    // APP_INFO.feature_flags is an object like { "flag.name": true/false }
    return window.APP_INFO?.feature_flags || {};
};

/**
 * Hook for managing feature flag overrides in Cloud SOAR
 * Reads flags from window.APP_INFO.feature_flags (set by backend)
 * Stores overrides in localStorage (same keys used by @sbu/hooks.featureflag)
 *
 * @param {'localStorage' | 'url'} mode - Storage mode for overrides
 */
export const useFeatureFlagManager = (mode) => {
    // Get flags from Cloud SOAR's APP_INFO global
    const appInfoFlags = useMemo(() => getAppInfoFlags(), []);

    // Use state to track localStorage overrides so changes trigger re-renders
    const [localStorageOverrides, setLocalStorageOverrides] = useState(() => ({
        forceOn: readLocalStorageFlags(FORCE_ON_KEY),
        forceOff: readLocalStorageFlags(FORCE_OFF_KEY),
        custom: readLocalStorageFlags(CUSTOM_FLAGS_KEY),
    }));

    // Get all flag names from APP_INFO + registered flags + localStorage overrides + custom flags
    const allFlagNames = useMemo(() => {
        const flagsFromAppInfo = Object.keys(appInfoFlags);
        const registeredFlagNames = getRegisteredFlagNames();

        // Combine all unique flag names and sort ascending
        const allFlags = new Set([
            ...flagsFromAppInfo,
            ...registeredFlagNames,
            ...localStorageOverrides.forceOn,
            ...localStorageOverrides.forceOff,
            ...localStorageOverrides.custom,
        ]);

        return Array.from(allFlags).sort((a, b) => a.localeCompare(b));
    }, [appInfoFlags, localStorageOverrides]);

    let forceOnFlags;
    let forceOffFlags;

    if (mode === 'url') {
        const urlFlags = readUrlFlags();
        forceOnFlags = urlFlags.enabledFlags;
        forceOffFlags = urlFlags.disabledFlags;
    } else {
        forceOnFlags = localStorageOverrides.forceOn;
        forceOffFlags = localStorageOverrides.forceOff;
    }

    const flagStates = useMemo(() => {
        const appInfoFlagNames = Object.keys(appInfoFlags);
        const registeredFlagNames = getRegisteredFlagNames();

        return allFlagNames.map((flagName) => {
            const isInAppInfo = appInfoFlagNames.includes(flagName);
            const isRegistered = isRegisteredFlag(flagName);
            const registeredMeta = isRegistered ? getRegisteredFlag(flagName) : null;

            // Backend state comes from APP_INFO.feature_flags
            // For registered flags not yet in backend, use the registry default
            const backendEnabled = isInAppInfo
                ? !!appInfoFlags[flagName]
                : registeredMeta?.defaultEnabled ?? false;

            const isForcedOn = forceOnFlags.includes(flagName);
            const isForcedOff = forceOffFlags.includes(flagName);

            // Custom = not from backend AND not registered (user manually added)
            const isCustom = !isInAppInfo && !isRegistered;

            let overrideState = 'none';
            let effectiveState = backendEnabled;

            if (isForcedOn) {
                overrideState = 'forceOn';
                effectiveState = true;
            } else if (isForcedOff) {
                overrideState = 'forceOff';
                effectiveState = false;
            }

            return {
                name: flagName,
                description: registeredMeta?.description || null,
                backendEnabled,
                overrideState,
                effectiveState,
                isCustom,
                isRegistered,
                isInBackend: isInAppInfo,
            };
        });
    }, [allFlagNames, appInfoFlags, forceOnFlags, forceOffFlags]);

    const forceEnable = useCallback(
        (flagName) => {
            if (mode === 'localStorage') {
                setLocalStorageOverrides((prev) => {
                    const newForceOff = prev.forceOff.filter((f) => f !== flagName);
                    const newForceOn = prev.forceOn.includes(flagName)
                        ? prev.forceOn
                        : [...prev.forceOn, flagName];

                    writeLocalStorageFlags(FORCE_OFF_KEY, newForceOff);
                    writeLocalStorageFlags(FORCE_ON_KEY, newForceOn);

                    return { ...prev, forceOn: newForceOn, forceOff: newForceOff };
                });
            }
        },
        [mode]
    );

    const forceDisable = useCallback(
        (flagName) => {
            if (mode === 'localStorage') {
                setLocalStorageOverrides((prev) => {
                    const newForceOn = prev.forceOn.filter((f) => f !== flagName);
                    const newForceOff = prev.forceOff.includes(flagName)
                        ? prev.forceOff
                        : [...prev.forceOff, flagName];

                    writeLocalStorageFlags(FORCE_ON_KEY, newForceOn);
                    writeLocalStorageFlags(FORCE_OFF_KEY, newForceOff);

                    return { ...prev, forceOn: newForceOn, forceOff: newForceOff };
                });
            }
        },
        [mode]
    );

    const resetFlag = useCallback(
        (flagName) => {
            if (mode === 'localStorage') {
                setLocalStorageOverrides((prev) => {
                    const newForceOn = prev.forceOn.filter((f) => f !== flagName);
                    const newForceOff = prev.forceOff.filter((f) => f !== flagName);

                    writeLocalStorageFlags(FORCE_ON_KEY, newForceOn);
                    writeLocalStorageFlags(FORCE_OFF_KEY, newForceOff);

                    return { ...prev, forceOn: newForceOn, forceOff: newForceOff };
                });
            }
        },
        [mode]
    );

    const resetAll = useCallback(() => {
        if (mode === 'localStorage') {
            writeLocalStorageFlags(FORCE_ON_KEY, []);
            writeLocalStorageFlags(FORCE_OFF_KEY, []);
            setLocalStorageOverrides((prev) => ({ ...prev, forceOn: [], forceOff: [] }));
        }
    }, [mode]);

    const addToUrl = useCallback((flagName, enable) => {
        const url = new URL(window.location.href);
        const currentE = url.searchParams.get('e');

        const existingFlags = currentE
            ? currentE
                  .split(',')
                  .map((f) => f.trim())
                  .filter(Boolean)
            : [];

        const newFlag = enable ? flagName : `-${flagName}`;

        const existingIndex = existingFlags.findIndex(
            (f) => f === flagName || f === `-${flagName}`
        );

        if (existingIndex !== -1) {
            existingFlags[existingIndex] = newFlag;
        } else {
            existingFlags.push(newFlag);
        }

        url.searchParams.set('e', existingFlags.join(','));
        window.history.replaceState({}, '', url.toString());

        return url.toString();
    }, []);

    const removeFromUrl = useCallback((flagName) => {
        const url = new URL(window.location.href);
        const currentE = url.searchParams.get('e');

        if (!currentE) {
            return null;
        }

        const existingFlags = currentE
            .split(',')
            .map((f) => f.trim())
            .filter(Boolean);

        const filteredFlags = existingFlags.filter(
            (f) => f !== flagName && f !== `-${flagName}`
        );

        if (filteredFlags.length === 0) {
            url.searchParams.delete('e');
        } else {
            url.searchParams.set('e', filteredFlags.join(','));
        }

        window.history.replaceState({}, '', url.toString());

        return url.toString();
    }, []);

    const copyCurrentUrl = useCallback(() => {
        return window.location.href;
    }, []);

    const addFlag = useCallback(
        (flagName) => {
            if (!flagName || allFlagNames.includes(flagName)) {
                return false;
            }

            setLocalStorageOverrides((prev) => {
                const newCustom = [...prev.custom, flagName];
                writeLocalStorageFlags(CUSTOM_FLAGS_KEY, newCustom);
                return { ...prev, custom: newCustom };
            });

            return true;
        },
        [allFlagNames]
    );

    const removeFlag = useCallback((flagName) => {
        setLocalStorageOverrides((prev) => {
            const newCustom = prev.custom.filter((f) => f !== flagName);
            const newForceOn = prev.forceOn.filter((f) => f !== flagName);
            const newForceOff = prev.forceOff.filter((f) => f !== flagName);

            writeLocalStorageFlags(CUSTOM_FLAGS_KEY, newCustom);
            writeLocalStorageFlags(FORCE_ON_KEY, newForceOn);
            writeLocalStorageFlags(FORCE_OFF_KEY, newForceOff);

            return { custom: newCustom, forceOn: newForceOn, forceOff: newForceOff };
        });
    }, []);

    return {
        flags: flagStates,
        loading: false, // Synchronous read from APP_INFO
        forceEnable,
        forceDisable,
        resetFlag,
        resetAll,
        addToUrl,
        removeFromUrl,
        copyCurrentUrl,
        addFlag,
        removeFlag,
    };
};
